/**
 * The "Triage is updating" banner (Galley #38663, the owner's #38648). Renders nothing while
 * Triage answers normally. When a request gets a stopped worker's answer (`lib/updating.ts`), it
 * says so above the page, asks `/api/ready` every 10 s, and once the database is attached
 * refetches the queries on screen, so the page catches up without a reload (#39101).
 */
import { useEffect, useRef, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  PROBE_INTERVAL_MS,
  READY_PATH,
  isUpdating,
  noteReadyProbe,
  subscribe,
} from "@/lib/updating";

export function UpdatingBanner({
  probeIntervalMs = PROBE_INTERVAL_MS,
}: {
  /** Tests only: the production value is `PROBE_INTERVAL_MS`. */
  probeIntervalMs?: number;
} = {}) {
  const updating = useSyncExternalStore(subscribe, isUpdating, isUpdating);
  const queryClient = useQueryClient();
  const wasUpdating = useRef(false);

  useEffect(() => {
    if (wasUpdating.current && !updating) {
      void queryClient.invalidateQueries();
    }
    wasUpdating.current = updating;
  }, [updating, queryClient]);

  useEffect(() => {
    if (!updating) return;
    const id = window.setInterval(() => {
      fetch(READY_PATH, { credentials: "same-origin", cache: "no-store" })
        .then(noteReadyProbe)
        .catch(() => {
          // Not even the load balancer answered: still updating, try again next tick.
          noteReadyProbe(null);
        });
    }, probeIntervalMs);
    return () => window.clearInterval(id);
  }, [updating, probeIntervalMs]);

  if (!updating) return null;
  return (
    <div
      role="status"
      className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100"
    >
      <span className="font-medium">Triage is updating.</span> This page will catch up on its
      own when it&apos;s back, usually within a few minutes.
    </div>
  );
}
